import React from 'react'
import { Tag as AntdTag } from 'antd'
import styled from 'styled-components'

import type { TagProps } from './props'
import { TagColorType, TagVariantType } from './types'
import {
  generateBoldHoverStyles,
  generateBoldStyles,
  generateMinimalHoverStyles,
  generateMinimalStyles,
  generateSubtleHoverStyles,
  generateSubtleStyles,
} from './utils'

/* -------------------------------------------------------------------------
 * The ANTD half of the `src/antd/Tag` seam — and, unlike Button, Space and
 * Typography, this half is NOT antd's own component.
 *
 * `Tag` is one of the SIX exports `src/antd` shadows rather than re-exports:
 * long before the design toggle existed, the seam already replaced antd's tag
 * palette with this repo's own (`bold`/`subtle`/`minimal` × `TagColorType`,
 * computed with the `color` package in `./utils`). So "the toggle is off"
 * means THIS file, not `antd/es/tag` — everything below is the previous
 * `./index.tsx` moved here unchanged, so that `./index.tsx` can become the
 * dispatcher without altering a single rule.
 *
 * That also fixes what the parity test can prove:
 * `src/antd/__tests__/tag-seam-parity.test.tsx` renders the same JSX through
 * the dispatcher and through this module and demands byte-identical DOM —
 * i.e. the dispatcher is transparent. It cannot compare against `antd/es/tag`,
 * because the seam has never rendered that.
 * ------------------------------------------------------------------------- */

const TagVariantStyleMap = {
  bold: generateBoldStyles,
  subtle: generateSubtleStyles,
  minimal: generateMinimalStyles,
}

const TagVariantHoverStyleMap = {
  bold: generateBoldHoverStyles,
  subtle: generateSubtleHoverStyles,
  minimal: generateMinimalHoverStyles,
}

const StyledTag = styled(AntdTag)<{
  $variant: TagVariantType
  $color: TagColorType
  $withHover?: boolean
}>(
  ({ $variant, $color, $withHover }) => `
    padding: 2px 4px;
    border-radius: 4px;
    box-shadow: none;
    transition-duration: 0.2s;
    transition-property: background-color, color, border-color;
    font-size: 12px;
    line-height: 16px;
    display: inline-flex;
    align-items: center;
    ${TagVariantStyleMap[$variant]($color)}
    ${$withHover ? TagVariantHoverStyleMap[$variant]($color) : ''}
    .anticon + span,
    img + span {
      margin-left: 4px;
    }
  `
)

export function Tag({
  variant = 'subtle',
  color = 'gray',
  withHover = true,
  ...props
}: TagProps) {
  return (
    <StyledTag
      {...props}
      $variant={variant}
      $color={color}
      $withHover={withHover}
    />
  )
}
